import React, { useEffect, useState } from 'react'
import { ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { CustomContentModal } from '../../../components/CustomContentModal'
import type { TariffHistoryAttachment } from '../../../apis/types'
import { downloadTariffAttachmentApi } from '../../../apis/apis'

export const TariffDocumentViewerModal: React.FC<{
    attachment: TariffHistoryAttachment | null
    onClose: () => void
}> = ({ attachment, onClose }) => {
    const [objectUrl, setObjectUrl] = useState<string | undefined>()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (attachment == null) {
            setObjectUrl(undefined)
            setError(null)
            return
        }

        const controller = new AbortController()
        setLoading(true)
        setError(null)

        void downloadTariffAttachmentApi(attachment.cuid, 'inline', controller.signal)
            .then(({ data, contentType }) => {
                if (controller.signal.aborted) return
                const blob = new Blob([data], { type: contentType ?? 'application/pdf' })
                setObjectUrl(URL.createObjectURL(blob))
            })
            .catch(() => {
                if (!controller.signal.aborted) {
                    setError('Unable to load document')
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false)
            })

        return () => {
            controller.abort()
            setObjectUrl((prev) => {
                if (prev != null) URL.revokeObjectURL(prev)
                return undefined
            })
        }
    }, [attachment])

    const handleDownload = (): void => {
        if (attachment == null) return
        void downloadTariffAttachmentApi(attachment.cuid, 'attachment').then(({ data, filename, contentType }) => {
            const blob = new Blob([data], { type: contentType ?? 'application/pdf' })
            const url = URL.createObjectURL(blob)
            const anchor = document.createElement('a')
            anchor.href = url
            anchor.download = filename
            document.body.appendChild(anchor)
            anchor.click()
            anchor.remove()
            URL.revokeObjectURL(url)
        })
    }

    return (
        <CustomContentModal isOpen={attachment != null} onClose={onClose} className="w-full max-w-5xl">
            <div className="flex h-[85vh] w-full flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-slate-900">
                <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-2 dark:border-slate-700">
                    <h3 className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                        {attachment?.label ?? 'Document'}
                    </h3>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleDownload}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                            <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
                            Download
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
                            aria-label="Close"
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </div>
                </div>
                <div className="min-h-0 flex-1 bg-slate-50 dark:bg-slate-950">
                    {loading ? (
                        <div className="flex h-full items-center justify-center text-sm text-slate-500">
                            Loading document…
                        </div>
                    ) : null}
                    {error != null ? (
                        <div className="flex h-full items-center justify-center text-sm text-red-600">{error}</div>
                    ) : null}
                    {objectUrl != null && !loading && error == null ? (
                        <iframe title={attachment?.label ?? 'Document'} src={objectUrl} className="h-full w-full" />
                    ) : null}
                </div>
            </div>
        </CustomContentModal>
    )
}
